import { createClient, type SupabaseClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

const REMEMBER_ME_KEY = 'auth.rememberMe'
const LAST_ACTIVITY_KEY = 'auth.lastActivity'
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

/** Call before signIn to set whether session should persist. */
export function setRememberMe(remember: boolean) {
  try {
    localStorage.setItem(REMEMBER_ME_KEY, remember ? 'true' : 'false')
  } catch {
    // ignore
  }
}

/** Storage adapter: localStorage when remember me, sessionStorage otherwise. */
function createRememberMeStorage() {
  const getStorage = () => {
    try {
      const remember = localStorage.getItem(REMEMBER_ME_KEY) !== 'false'
      return remember ? localStorage : sessionStorage
    } catch {
      return localStorage
    }
  }
  return {
    getItem: (key: string) => getStorage().getItem(key),
    setItem: (key: string, value: string) => getStorage().setItem(key, value),
    removeItem: (key: string) => {
      try {
        localStorage.removeItem(key)
        sessionStorage.removeItem(key)
      } catch {
        // ignore
      }
    },
  }
}

export function updateLastActivity() {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()))
  } catch {
    // ignore
  }
}

export function shouldExpireSession(): boolean {
  try {
    if (localStorage.getItem(REMEMBER_ME_KEY) === 'false') return false
    const last = localStorage.getItem(LAST_ACTIVITY_KEY)
    if (!last) return false
    const ts = parseInt(last, 10)
    if (Number.isNaN(ts)) return false
    return Date.now() - ts > SEVEN_DAYS_MS
  } catch {
    return false
  }
}

function createMockSupabase() {
  if (import.meta.env.DEV) {
    console.warn('[Gigafish] Supabase env not set. Running with a mock client. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to .env to enable auth and storage.')
  }
  const noop = () => {}
  return {
    auth: {
      getSession: async () => ({ data: { session: null }, error: null }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: noop } } }),
      signInWithPassword: async (opts: { email?: string; password?: string }) => ({
        data: {
          user: opts?.email ? { id: 'mock-user', email: opts.email, user_metadata: {} } : null,
          session: null,
        },
        error: null,
      }),
      signUp: async (opts: { email?: string; password?: string }) => ({
        data: {
          user: opts?.email ? { id: 'mock-user', email: opts.email, user_metadata: {} } : null,
          session: null,
        },
        error: null,
      }),
      signInWithOtp: async (opts: { email?: string }) => ({
        data: {
          user: opts?.email ? { id: 'mock-user', email: opts.email, user_metadata: {} } : null,
          session: null,
        },
        error: null,
      }),
      signOut: async () => ({ error: null }),
      resetPasswordForEmail: async () => ({ error: null }),
      updateUser: async () => ({ error: null }),
      getUser: async () => ({ data: { user: null }, error: null })
    },
    from: () => {
      const emptyResult = { data: [], error: null }
      const singleNull = { data: null, error: null }
      const chain = {
        select: () => chain,
        eq: () => chain,
        order: () => chain,
        maybeSingle: () => Promise.resolve(singleNull),
        single: () => Promise.resolve(singleNull),
        then: (resolve: (v: typeof emptyResult) => void) => {
          resolve(emptyResult)
          return Promise.resolve(emptyResult)
        },
        insert: () => ({ select: () => ({ single: () => Promise.resolve({ data: {}, error: null }) }) }),
        update: () => ({
          eq: () => Promise.resolve({ error: null }),
          select: () => ({ single: () => Promise.resolve({ data: {}, error: null }) }),
        }),
        delete: () => ({ eq: () => Promise.resolve({ error: null }) }),
      }
      return chain
    },
    storage: {
      from: () => ({
        upload: async () => ({ data: { path: '' }, error: null }),
        getPublicUrl: (_path: string) => ({ data: { publicUrl: '' } }),
        remove: async () => ({ error: null })
      })
    },
    functions: {
      invoke: async () => ({ data: null, error: { message: 'Supabase not configured' } })
    },
    rpc: async () => ({ data: null, error: null }),
  } as unknown as SupabaseClient
}

/**
 * Invoke contact-form edge function via raw fetch so we reliably get the response body
 * (including error messages) regardless of status code.
 */
export async function invokeContactForm(body: Record<string, unknown>): Promise<{ success?: boolean; email_sent?: boolean }> {
  const url = import.meta.env.VITE_SUPABASE_URL
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY
  if (!url || !key) throw new Error('Supabase not configured')
  const res = await fetch(`${url}/functions/v1/contact-form`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${key}`,
    },
    body: JSON.stringify(body),
  })
  const data = (await res.json().catch(() => ({}))) as { success?: boolean; email_sent?: boolean; error?: string }
  if (!res.ok) {
    throw new Error(data?.error ?? 'Submission failed')
  }
  return data
}

export const supabase = (supabaseUrl && supabaseAnonKey)
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storage: createRememberMeStorage(),
      }
    })
  : createMockSupabase()

// Database types (you can generate these with Supabase CLI)
export interface Database {
  public: {
    Tables: Record<string, never>
    Views: {
      [_ in never]: never
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {
      [_ in never]: never
    }
  }
}

export type Tables<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Row']
export type Enums<T extends keyof Database['public']['Enums']> = Database['public']['Enums'][T]
